// withTimeout.js — race-a-promise-against-a-deadline helper.
//
// Why this exists:
//   Several page loads (Home, FriendCellar, PublicEvents, Shop, Admin
//   Users) trigger Firestore reads via getDocs / getDoc. On cold-start
//   network conditions (mobile data, captive portals, Firestore's
//   first connection round-trip), an individual read can hang for
//   30-60s before the network layer gives up. The UI sits on a spinner
//   the whole time with no feedback to the user.
//
//   The pattern Home.jsx adopted (2026-05-06) is to race each read
//   against an 8-15s timeout that rejects with a clear error. The
//   caller catches it and renders an empty state or a "couldn't load,
//   tap to retry" message instead of spinning forever.
//
//   This helper consolidates the pattern so every caller doesn't
//   re-inline `Promise.race + setTimeout + reject(new Error(...))`.
//
// Usage:
//   import { withTimeout } from '../utils/withTimeout.js'
//
//   const snap = await withTimeout(
//     getDocs(query(...)),
//     8000,
//     'events-feed-load',
//   )
//
//   The timeout error message includes the label so Sentry / log
//   triage can identify WHICH read timed out.

const DEFAULT_TIMEOUT_MS = 8000

/**
 * Race a promise against a deadline. If the promise resolves first,
 * its value is returned. If the deadline elapses first, the returned
 * promise rejects with an Error whose message is `{label}-timeout`
 * (or just `timeout` when no label is given).
 *
 * @param {Promise<T>} promise — the promise to race
 * @param {number} [ms=8000] — timeout in milliseconds
 * @param {string} [label] — short identifier used in the error message
 *   (e.g. 'events-feed-load'). Defaults to the generic `timeout`.
 * @returns {Promise<T>}
 */
export function withTimeout(promise, ms = DEFAULT_TIMEOUT_MS, label = '') {
  let timer
  const deadline = new Promise((_, reject) => {
    timer = setTimeout(() => {
      const tag = label ? `${label}-timeout` : 'timeout'
      reject(new Error(tag))
    }, ms)
  })
  return Promise.race([promise, deadline]).finally(() => clearTimeout(timer))
}

/**
 * Returns true if the given error came from `withTimeout`. Useful for
 * the `catch` branch that distinguishes timeouts from real errors
 * (timeouts → "show empty state + retry"; real errors → "log to
 * Sentry + show error message").
 */
export function isTimeoutError(err) {
  return err instanceof Error && /-?timeout$/.test(err.message)
}
